/**
 * Sidebar Component
 *
 * Left panel containing:
 * - Collapsible Channels section
 * - Collapsible Direct Messages section
 * - Collapsible Voice Channels section
 * - Fixed User Panel at bottom
 *
 * Supports two layout modes (user setting):
 * - "stacked": All sections visible, each collapsible
 * - "tabbed": Three tabs, one section visible at a time
 */

import { useState } from 'react';
import { useAppStore } from '@/store/app.store';
import { useUserSettings } from '@/hooks/useUserSettings';
import ChannelList from '@/components/channels/ChannelList';
import CreateChannelModal from '@/components/channels/CreateChannelModal';
import DMThreadList from '@/components/dms/DMThreadList';
import StartDMModal from '@/components/dms/StartDMModal';
import UserPanel from './UserPanel';
import { DigitalOfficeSection } from '@/components/voice/DigitalOfficeSection';
import { ChevronDown, ChevronRight, Plus, Hash, MessageCircle, Building2 } from 'lucide-react';
import { isElectron, isMacOS } from '@/services/platform';

interface SidebarProps {
  onOpenSettings: () => void;
  fileDragActive?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  children: React.ReactNode;
  badge?: number;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  isExpanded,
  onToggle,
  onAdd,
  children,
  badge,
}) => (
  <div className="border-b border-dark-border-primary/50">
    <div className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] font-semibold text-dark-text-muted">
      <button
        onClick={onToggle}
        className="flex flex-1 min-w-0 items-center gap-2 text-left hover:text-gray-200 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        {icon}
        <span className="flex-1 text-left">{title}</span>
        {badge !== undefined && badge > 0 && (
          <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
      {onAdd && (
        <button
          onClick={onAdd}
          className="p-1 hover:bg-dark-bg-hover hover:text-gray-200 rounded-md transition-all duration-150"
          title={`Добави ${title.toLowerCase()}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
    {isExpanded && <div className="pb-2">{children}</div>}
  </div>
);

type SidebarTab = 'channels' | 'dms' | 'voice';

const Sidebar = ({ onOpenSettings, fileDragActive = false }: SidebarProps) => {
  const { setActiveView } = useAppStore();
  const { data: userSettings } = useUserSettings();
  const sidebarLayout = userSettings?.sidebar_layout ?? 'stacked';

  // Section expansion state (stacked mode)
  const [sectionsExpanded, setSectionsExpanded] = useState({
    channels: true,
    dms: true,
    voiceChannels: true,
  });

  // Active tab state (tabbed mode)
  const [activeTab, setActiveTab] = useState<SidebarTab>('channels');

  // Modal state
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showStartDM, setShowStartDM] = useState(false);

  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChannelClick = () => {
    setActiveView('channels');
  };

  const handleDMClick = () => {
    setActiveView('dms');
  };

  const tabs: { key: SidebarTab; label: string; icon: React.ReactNode; title?: string; onAdd?: () => void }[] = [
    { key: 'channels', label: 'Канали', icon: <Hash className="w-4 h-4" />, onAdd: () => setShowCreateChannel(true) },
    { key: 'dms', label: 'Лични', icon: <MessageCircle className="w-4 h-4" />, onAdd: () => setShowStartDM(true) },
    { key: 'voice', label: 'Офис', icon: <Building2 className="w-4 h-4" />, title: 'Гласови стаи и обаждания' },
  ];

  return (
    <div className="h-full bg-gradient-to-b from-dark-bg-secondary to-dark-bg-primary text-white flex flex-col">
      {/* macOS Electron: drag spacer for traffic lights. Hidden during file drags. */}
      {isElectron() && isMacOS() && (
        <div className={`h-10 flex-shrink-0${fileDragActive ? '' : ' electron-drag'}`} />
      )}

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        {sidebarLayout === 'tabbed' ? (
          <>
            {/* Tab Bar */}
            <div className="flex border-b border-dark-border-primary/50 sticky top-0 bg-dark-bg-secondary z-10">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  title={tab.title}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-[11px] font-semibold transition-colors ${
                    activeTab === tab.key
                      ? 'text-white border-b-2 border-blue-500'
                      : 'text-dark-text-muted hover:text-gray-200'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
              {/* Contextual + button for active tab */}
              {tabs.find(t => t.key === activeTab)?.onAdd && (
                <button
                  onClick={tabs.find(t => t.key === activeTab)!.onAdd}
                  className="flex items-center px-2 text-dark-text-muted hover:text-gray-200 transition-colors"
                  title="Добави"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Tab Content */}
            <div className="pb-2">
              {activeTab === 'channels' && (
                <div onClick={handleChannelClick}>
                  <ChannelList hideHeader />
                </div>
              )}
              {activeTab === 'dms' && (
                <div onClick={handleDMClick}>
                  <DMThreadList />
                </div>
              )}
              {activeTab === 'voice' && (
                <DigitalOfficeSection>
                  {({ content, onAdd }) => (
                    <>
                      {onAdd && (
                        <div className="flex justify-end px-2 pt-2">
                          <button
                            onClick={onAdd}
                            className="p-1 text-dark-text-muted hover:text-gray-200 hover:bg-dark-bg-hover rounded-md transition-all duration-150"
                            title="Добави стая"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {content}
                    </>
                  )}
                </DigitalOfficeSection>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Stacked Layout (default) */}
            <CollapsibleSection
              title="Канали"
              icon={<Hash className="w-4 h-4" />}
              isExpanded={sectionsExpanded.channels}
              onToggle={() => toggleSection('channels')}
              onAdd={() => setShowCreateChannel(true)}
            >
              <div onClick={handleChannelClick}>
                <ChannelList hideHeader />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Лични съобщения"
              icon={<MessageCircle className="w-4 h-4" />}
              isExpanded={sectionsExpanded.dms}
              onToggle={() => toggleSection('dms')}
              onAdd={() => setShowStartDM(true)}
            >
              <div onClick={handleDMClick}>
                <DMThreadList />
              </div>
            </CollapsibleSection>

            <DigitalOfficeSection>
              {({ title, icon, badge, content, onAdd }) => (
                <CollapsibleSection
                  title={title}
                  icon={icon}
                  badge={badge}
                  isExpanded={sectionsExpanded.voiceChannels}
                  onToggle={() => toggleSection('voiceChannels')}
                  onAdd={onAdd}
                >
                  {content}
                </CollapsibleSection>
              )}
            </DigitalOfficeSection>
          </>
        )}
      </div>

      {/* Fixed User Panel at Bottom */}
      <UserPanel onOpenSettings={onOpenSettings} />

      {/* Modals */}
      {showCreateChannel && (
        <CreateChannelModal onClose={() => setShowCreateChannel(false)} />
      )}
      {showStartDM && (
        <StartDMModal onClose={() => setShowStartDM(false)} />
      )}
    </div>
  );
};

export default Sidebar;
